import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  FigureElement,
  FigureHandle,
  MarkSpec,
  MarksHandle,
  ReadoutHandle,
  ReadoutRow,
} from '@learnforge/demo-render';
import type { GeometryContext } from '../types';
import type { GeometryKey } from '../locales/keys';
import type { Vec2, Triangle, CongruenceSpec } from '@learnforge/demo-math/geometry';
import {
  dist,
  add,
  sub,
  scale,
  normalize,
  radToDeg,
  isValidTriangle,
  realizeTriangle,
  ssaSolutions,
  centroid,
  circumcenter,
  incenter,
  orthocenter,
  heron,
  eulerLineCollinear,
} from '@learnforge/demo-math/geometry';
import { TRIANGLE_PRESETS } from '../data/triangle-presets';
import type { TriangleMode, CongruenceCriterion } from '../data/triangle-presets';

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

export interface InequalityResult {
  closes: boolean;
  apex?: Vec2;
  hingeGapAngle?: number;
}

/**
 * Inequality-mode reducer.
 * Given three side lengths, returns whether the triangle closes.
 * If it closes, returns the apex via realizeTriangle (SSS).
 * If it doesn't, exposes the hinge gap angle (the angle at the apex of the
 * longest side that would be needed — radians "beyond" the straight position).
 */
export function inequalityReduce(a: number, b: number, c: number): InequalityResult {
  if (isValidTriangle(a, b, c)) {
    const tri = realizeTriangle({ kind: 'SSS', sides: [a, b, c] });
    return { closes: true, apex: tri?.c ?? { x: a / 2, y: 0 } };
  }
  // Determine the longest side; the two shorter sides are the "arms".
  // Place the longest side as the base; the two arms swing from each end.
  // The hinge gap angle is expressed as asin((base - arm1 - arm2) / base),
  // which gives a positive value in (0, π/2] when arm1 + arm2 < base.
  const longestSide = Math.max(a, b, c);
  let base: number, arm1: number, arm2: number;
  if (longestSide === c) {
    base = c;
    arm1 = a;
    arm2 = b;
  } else if (longestSide === b) {
    base = b;
    arm1 = a;
    arm2 = c;
  } else {
    base = a;
    arm1 = b;
    arm2 = c;
  }
  // Gap: how much the two arms fall short of spanning the base.
  const gap = base - arm1 - arm2;
  // Express as angle: asin(gap / base) gives 0 for collinear (degenerate),
  // and a meaningful positive angle for truly failing triangles.
  const hingeGapAngle = gap > 0 ? Math.asin(Math.min(1, gap / base)) : 0;
  return { closes: false, hingeGapAngle };
}

export interface HingeArmAngles {
  /** Angle (radians, from +x axis) of the arm swung from the left end of the base. */
  angleA: number;
  /** Angle (radians, from -x axis, i.e. measured inward) of the arm swung from the right end. */
  angleB: number;
  /** True when the two arms can meet (valid triangle inequality). */
  closes: boolean;
  /** Linear gap between arm tips when they cannot meet (base − arm1 − arm2). Zero when closes. */
  linearGap: number;
  /** The longest side (used as the base). */
  base: number;
  /** Length of the arm from the left end. */
  arm1: number;
  /** Length of the arm from the right end. */
  arm2: number;
}

/**
 * Derive the final arm angles for the inequality-mode hinge given three side lengths.
 *
 * Convention: the longest side is laid as the base.  arm1 hangs from the left
 * end, arm2 from the right end.
 *
 * Closing case: angles derived from the law of cosines (the interior angles of
 * the valid triangle at the base vertices).
 *
 * Failing case: both arms are laid flat along the base (angleA = 0, angleB = 0,
 * i.e. pointing directly toward the opposite end), exposing a linear gap equal
 * to base − arm1 − arm2.
 */
export function deriveHingeArmAngles(a: number, b: number, c: number): HingeArmAngles {
  // Identify longest side as base
  let base: number;
  let arm1: number;
  let arm2: number;
  if (c >= a && c >= b) {
    base = c;
    arm1 = a;
    arm2 = b;
  } else if (b >= a && b >= c) {
    base = b;
    arm1 = a;
    arm2 = c;
  } else {
    base = a;
    arm1 = b;
    arm2 = c;
  }

  if (isValidTriangle(arm1, arm2, base)) {
    // Law of cosines for the interior angle at the left end (angle A of the triangle):
    //   cos(A) = (base² + arm1² − arm2²) / (2 · base · arm1)
    const cosA = (base * base + arm1 * arm1 - arm2 * arm2) / (2 * base * arm1);
    const angleA = Math.acos(Math.max(-1, Math.min(1, cosA)));

    // Interior angle at the right end (angle B):
    //   cos(B) = (base² + arm2² − arm1²) / (2 · base · arm2)
    const cosB = (base * base + arm2 * arm2 - arm1 * arm1) / (2 * base * arm2);
    const angleB = Math.acos(Math.max(-1, Math.min(1, cosB)));

    return { angleA, angleB, closes: true, linearGap: 0, base, arm1, arm2 };
  }

  // Failing case: arms laid flat, gap = base − arm1 − arm2
  const linearGap = base - arm1 - arm2;
  return { angleA: 0, angleB: 0, closes: false, linearGap, base, arm1, arm2 };
}

export interface LockedParts {
  lockedSides: string[];
  lockedAngles: string[];
}

/**
 * Congruence-mode model: given a spec, return which parts are locked
 * (by name: 'sideAB', 'sideBC', 'sideAC', 'angleA', 'angleB', 'angleC').
 */
export function congruenceLockedParts(criterion: CongruenceCriterion): LockedParts {
  switch (criterion) {
    case 'SSS':
      return { lockedSides: ['sideAB', 'sideBC', 'sideAC'], lockedAngles: [] };
    case 'SAS':
      // Two sides and included angle: AB, AC, angleA
      return { lockedSides: ['sideAB', 'sideAC'], lockedAngles: ['angleA'] };
    case 'ASA':
      // Included side AB, angles at A and B
      return { lockedSides: ['sideAB'], lockedAngles: ['angleA', 'angleB'] };
    case 'AAS':
      // Two angles (A, B) and one non-included side (BC opposite A is incorrect —
      // AAS uses side opposite one of the angles; here BC opposite A)
      return { lockedSides: ['sideBC'], lockedAngles: ['angleA', 'angleB'] };
    case 'SSA':
      // Two sides (AB, BC) and angle at A
      return { lockedSides: ['sideAB', 'sideBC'], lockedAngles: ['angleA'] };
  }
}

/**
 * Verify that a rigid-body translate preserves all side lengths.
 * Returns true if all three side lengths are unchanged after applying dx/dy offset.
 */
export function rigidMotionPreservesSides(tri: Triangle, dx: number, dy: number): boolean {
  const translated: Triangle = {
    a: { x: tri.a.x + dx, y: tri.a.y + dy },
    b: { x: tri.b.x + dx, y: tri.b.y + dy },
    c: { x: tri.c.x + dx, y: tri.c.y + dy },
  };
  const eps = 1e-9;
  return (
    Math.abs(dist(tri.a, tri.b) - dist(translated.a, translated.b)) < eps &&
    Math.abs(dist(tri.b, tri.c) - dist(translated.b, translated.c)) < eps &&
    Math.abs(dist(tri.a, tri.c) - dist(translated.a, translated.c)) < eps
  );
}

export interface CentersPayload {
  centroid: Vec2;
  circumcenter: Vec2 | null;
  incenter: Vec2;
  orthocenter: Vec2;
  eulerLine: boolean;
}

/**
 * Compute the four triangle centers and whether the Euler line should be shown.
 * The Euler line is shown ONLY when all three of centroid + circumcenter + orthocenter toggles are on
 * AND circumcenter is non-null (i.e. the triangle is non-degenerate).
 */
export function buildCentersPayload(
  tri: Triangle,
  showCentroid: boolean,
  showCircumcenter: boolean,
  showOrthocenter: boolean,
): CentersPayload {
  const cc = circumcenter(tri);
  return {
    centroid: centroid(tri),
    circumcenter: cc,
    incenter: incenter(tri),
    orthocenter: orthocenter(tri),
    eulerLine: showCentroid && showCircumcenter && showOrthocenter && eulerLineCollinear(tri, 1e-6),
  };
}

// ============================================================================
// Internal geometry helpers
// ============================================================================

const STAGE_W = 800;
const STAGE_H = 460;

/** Default free-drag triangle vertices (centered in stage) */
const DEFAULT_VERTICES: [Vec2, Vec2, Vec2] = [
  { x: 240, y: 350 },
  { x: 560, y: 350 },
  { x: 380, y: 130 },
];

const CRITERIA: readonly CongruenceCriterion[] = ['SSS', 'SAS', 'ASA', 'AAS', 'SSA'];

/** Compute the three interior angles of a triangle in degrees */
function triangleAngles(tri: Triangle): [number, number, number] {
  const ab = dist(tri.a, tri.b);
  const bc = dist(tri.b, tri.c);
  const ac = dist(tri.a, tri.c);
  const cosA = (ab * ab + ac * ac - bc * bc) / (2 * ab * ac);
  const cosB = (ab * ab + bc * bc - ac * ac) / (2 * ab * bc);
  const cosC = (bc * bc + ac * ac - ab * ab) / (2 * bc * ac);
  return [
    radToDeg(Math.acos(Math.max(-1, Math.min(1, cosA)))),
    radToDeg(Math.acos(Math.max(-1, Math.min(1, cosB)))),
    radToDeg(Math.acos(Math.max(-1, Math.min(1, cosC)))),
  ];
}

/** Scale-and-center a realized triangle (canonical coords) to the stage */
function placeTriangleOnStage(raw: Triangle): Triangle {
  const cx = (raw.a.x + raw.b.x + raw.c.x) / 3;
  const cy = (raw.a.y + raw.b.y + raw.c.y) / 3;
  // Find bounding box
  const xs = [raw.a.x, raw.b.x, raw.c.x];
  const ys = [raw.a.y, raw.b.y, raw.c.y];
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const bbW = maxX - minX || 1;
  const bbH = maxY - minY || 1;
  const targetW = STAGE_W * 0.55;
  const targetH = STAGE_H * 0.55;
  const scale_ = Math.min(targetW / bbW, targetH / bbH);
  const stageCx = STAGE_W / 2;
  const stageCy = STAGE_H / 2;
  const translate = (v: Vec2): Vec2 => ({
    x: stageCx + (v.x - cx) * scale_,
    y: stageCy + (v.y - cy) * scale_,
  });
  return { a: translate(raw.a), b: translate(raw.b), c: translate(raw.c) };
}

/** Midpoint of two Vec2s */
function midpoint(a: Vec2, b: Vec2): Vec2 {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Realize the canonical congruence triangle(s) for a criterion, placed on the
 * stage. For SSA the ambiguous case yields two candidates; for everything else
 * a single representative triangle. Pure — used by `initialState`, criterion
 * changes, presets, and reset.
 */
function realizeCongruence(criterion: CongruenceCriterion): {
  congruenceTri: Triangle | null;
  ssaCandidates: Triangle[];
} {
  if (criterion === 'SSA') {
    const raw = ssaSolutions(4, 6, 30);
    const ssaCandidates = raw.map((t) => placeTriangleOnStage(t));
    return { congruenceTri: ssaCandidates[0] ?? null, ssaCandidates };
  }
  let spec: CongruenceSpec;
  switch (criterion) {
    case 'SSS':
      spec = { kind: 'SSS', sides: [5, 6, 7] };
      break;
    case 'SAS':
      spec = { kind: 'SAS', sides: [5, 6], angleDeg: 60 };
      break;
    case 'ASA':
      spec = { kind: 'ASA', anglesDeg: [60, 70], side: 5 };
      break;
    case 'AAS':
      spec = { kind: 'AAS', anglesDeg: [60, 70], side: 4 };
      break;
    default:
      spec = { kind: 'SSS', sides: [5, 6, 7] };
  }
  const raw = realizeTriangle(spec);
  return { congruenceTri: raw ? placeTriangleOnStage(raw) : null, ssaCandidates: [] };
}

/**
 * Build locked-part mark specs for the primary congruence triangle `t`.
 * Shared between the SSA and non-SSA branches.
 */
function buildCongruenceMarkSpecs(t: Triangle, criterion: CongruenceCriterion): MarkSpec[] {
  const specs: MarkSpec[] = [];
  const locked = congruenceLockedParts(criterion);
  for (let i = 0; i < locked.lockedSides.length; i++) {
    const sideName = locked.lockedSides[i]!;
    const tickCount = (i + 1) as 1 | 2 | 3;
    if (sideName === 'sideAB') {
      specs.push({ kind: 'tick', id: `lock-${sideName}`, a: t.a, b: t.b, count: tickCount });
    } else if (sideName === 'sideBC') {
      specs.push({ kind: 'tick', id: `lock-${sideName}`, a: t.b, b: t.c, count: tickCount });
    } else if (sideName === 'sideAC') {
      specs.push({ kind: 'tick', id: `lock-${sideName}`, a: t.a, b: t.c, count: tickCount });
    }
  }
  for (let i = 0; i < locked.lockedAngles.length; i++) {
    const angName = locked.lockedAngles[i]!;
    const strokes = (i + 1) as 1 | 2 | 3;
    if (angName === 'angleA') {
      specs.push({
        kind: 'angle',
        id: `lock-${angName}`,
        vertex: t.a,
        from: t.b,
        to: t.c,
        strokes,
      });
    } else if (angName === 'angleB') {
      specs.push({
        kind: 'angle',
        id: `lock-${angName}`,
        vertex: t.b,
        from: t.a,
        to: t.c,
        strokes,
      });
    } else if (angName === 'angleC') {
      specs.push({
        kind: 'angle',
        id: `lock-${angName}`,
        vertex: t.c,
        from: t.a,
        to: t.b,
        strokes,
      });
    }
  }
  return specs;
}

// ============================================================================
// State shape
// ============================================================================

interface TriangleState {
  mode: TriangleMode;
  showMeasurements: boolean;
  showCentroid: boolean;
  showCircumcenter: boolean;
  showIncenter: boolean;
  showOrthocenter: boolean;
  congruenceCriterion: CongruenceCriterion;

  // free-drag vertices
  vA: Vec2;
  vB: Vec2;
  vC: Vec2;

  // inequality side lengths
  inequalityA: number;
  inequalityB: number;
  inequalityC: number;

  // congruence sticky positions (the rigid-translate drag mutates these in
  // place; criterion changes / presets / reset re-realize them). Held in state
  // so a drag is not snapped back to the canonical pose on re-render.
  congruenceTri: Triangle | null;
  ssaCandidates: Triangle[];
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): ExperimentSpec<TriangleState, GeometryKey, GeometryContext> {
  // Closure-stashed handles + controller created in setup():
  let figure: FigureHandle;
  let marks: MarksHandle;
  let readout: ReadoutHandle;
  let controllerRef: HarnessController<TriangleState>;

  // Transient view state for the inequality hinge sweep. NOT part of `state`:
  // it is in-flight animation progress (0→1) driven imperatively via the
  // controller's tracked rAF, exactly mirroring the bespoke mount which
  // replayed the 0→1 sweep on every inequality input change. `render` reads it
  // but never starts the animation (render runs on unrelated re-renders too).
  let hingeT = 1;
  let hingeRafId: number | null = null;

  function cancelHingeAnimation(): void {
    if (hingeRafId !== null && controllerRef) {
      controllerRef.cancelRaf(hingeRafId);
      hingeRafId = null;
    }
  }

  /**
   * Kick off the 400ms ease-out hinge sweep (t: 0 → 1), re-rendering each frame
   * via the controller. Mirrors the bespoke `renderInequality()` animation.
   */
  function startHingeAnimation(): void {
    if (!controllerRef) return;
    cancelHingeAnimation();
    const ANIM_DURATION = 400; // ms
    let startTime: number | null = null;
    hingeT = 0;

    const frame = (now: number): void => {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;
      const rawT = Math.min(1, elapsed / ANIM_DURATION);
      hingeT = rawT;
      controllerRef.requestRender();
      if (rawT < 1) {
        hingeRafId = controllerRef.raf(frame);
      } else {
        hingeRafId = null;
      }
    };

    hingeRafId = controllerRef.raf(frame);
  }

  function defaultState(advanced: boolean): TriangleState {
    void advanced;
    const { congruenceTri, ssaCandidates } = realizeCongruence('SSS');
    return {
      mode: 'free-drag',
      showMeasurements: true,
      showCentroid: false,
      showCircumcenter: false,
      showIncenter: false,
      showOrthocenter: false,
      congruenceCriterion: 'SSS',
      vA: { ...DEFAULT_VERTICES[0] },
      vB: { ...DEFAULT_VERTICES[1] },
      vC: { ...DEFAULT_VERTICES[2] },
      inequalityA: 3,
      inequalityB: 4,
      inequalityC: 5,
      congruenceTri,
      ssaCandidates,
    };
  }

  /**
   * Apply a preset entry to the model. PURE. Respects advancedOnly vs advanced.
   * The `euler-tour` preset additionally turns on the three Euler-line centres
   * (centroid + circumcenter + orthocenter), matching the bespoke onPreset.
   */
  function applyPresetLocal(
    state: TriangleState,
    presetId: string,
    advanced: boolean,
  ): TriangleState {
    const preset = TRIANGLE_PRESETS.find((p) => p.id === presetId);
    if (!preset) return state;
    if (preset.advancedOnly && !advanced) return state;

    let next: TriangleState = { ...state, mode: preset.mode };

    if (preset.mode === 'free-drag' && preset.vertices) {
      next = {
        ...next,
        vA: { ...preset.vertices[0] },
        vB: { ...preset.vertices[1] },
        vC: { ...preset.vertices[2] },
      };
      if (presetId === 'euler-tour') {
        next = { ...next, showCentroid: true, showCircumcenter: true, showOrthocenter: true };
      }
    } else if (preset.mode === 'inequality' && preset.sides) {
      next = {
        ...next,
        inequalityA: preset.sides[0],
        inequalityB: preset.sides[1],
        inequalityC: preset.sides[2],
      };
    } else if (preset.mode === 'congruence' && preset.criterion) {
      const realized = realizeCongruence(preset.criterion);
      next = {
        ...next,
        congruenceCriterion: preset.criterion,
        congruenceTri: realized.congruenceTri,
        ssaCandidates: realized.ssaCandidates,
      };
    }

    return next;
  }

  return {
    labelKey: 'experiment.triangle-lab',
    demoId: '2d-geometry',
    advancedKey: 'triangle-lab',

    initialState(ctx): TriangleState {
      return defaultState(ctx.advanced);
    },

    applyPreset(state, presetId, ctx): TriangleState {
      const next = applyPresetLocal(state, presetId, ctx.advanced);
      // If the applied preset switched into inequality, replay the hinge sweep.
      if (next.mode === 'inequality' && next !== state) startHingeAnimation();
      return next;
    },

    setup(_ctx, controller) {
      controllerRef = controller;
      figure = controller.createFigure();
      marks = controller.createMarks();
      readout = controller.createReadout();
    },

    render(state: TriangleState, ctx): void {
      if (state.mode === 'free-drag') {
        renderFreeDrag(state, ctx.advanced);
      } else if (state.mode === 'inequality') {
        renderInequality(state);
      } else if (state.mode === 'congruence') {
        renderCongruence(state);
      }

      // --------------------------------------------------------------------
      // Free-drag mode
      // --------------------------------------------------------------------
      function renderFreeDrag(state: TriangleState, advanced: boolean): void {
        figure.update(buildFreeDragFigure(state, advanced));
        marks.update(buildFreeDragMarks(state));
        readout.update(buildFreeDragReadout(state));
      }

      function buildFreeDragFigure(state: TriangleState, advanced: boolean): FigureElement[] {
        const { vA, vB, vC } = state;
        const elems: FigureElement[] = [
          {
            kind: 'point',
            id: 'vA',
            at: vA,
            draggable: {},
            style: { className: 'geo-point-draggable' },
          },
          {
            kind: 'point',
            id: 'vB',
            at: vB,
            draggable: {},
            style: { className: 'geo-point-draggable' },
          },
          {
            kind: 'point',
            id: 'vC',
            at: vC,
            draggable: {},
            style: { className: 'geo-point-draggable' },
          },
          { kind: 'segment', id: 'seg-AB', from: 'vA', to: 'vB', style: { className: 'geo-line' } },
          { kind: 'segment', id: 'seg-BC', from: 'vB', to: 'vC', style: { className: 'geo-line' } },
          { kind: 'segment', id: 'seg-AC', from: 'vA', to: 'vC', style: { className: 'geo-line' } },
        ];

        if (advanced) {
          const tri: Triangle = { a: vA, b: vB, c: vC };
          const cg = centroid(tri);
          const cc = circumcenter(tri);
          const ic = incenter(tri);
          const oc = orthocenter(tri);

          if (state.showCentroid) {
            // Medians: A→midBC, B→midAC, C→midAB
            const mBC = midpoint(vB, vC);
            const mAC = midpoint(vA, vC);
            const mAB = midpoint(vA, vB);
            elems.push(
              { kind: 'point', id: 'mid-BC', at: mBC, style: { className: 'geo-point-main' } },
              { kind: 'point', id: 'mid-AC', at: mAC, style: { className: 'geo-point-main' } },
              { kind: 'point', id: 'mid-AB', at: mAB, style: { className: 'geo-point-main' } },
              {
                kind: 'segment',
                id: 'median-A',
                from: 'vA',
                to: 'mid-BC',
                style: { className: 'geo-line', dashed: true },
              },
              {
                kind: 'segment',
                id: 'median-B',
                from: 'vB',
                to: 'mid-AC',
                style: { className: 'geo-line', dashed: true },
              },
              {
                kind: 'segment',
                id: 'median-C',
                from: 'vC',
                to: 'mid-AB',
                style: { className: 'geo-line', dashed: true },
              },
              { kind: 'point', id: 'centroid', at: cg, style: { className: 'geo-point-main' } },
            );
          }

          if (state.showCircumcenter && cc) {
            // Perpendicular bisectors: midpoints already registered if centroid on, else add them
            const mBC = midpoint(vB, vC);
            const mAC = midpoint(vA, vC);
            const mAB = midpoint(vA, vB);
            elems.push(
              { kind: 'point', id: 'circ-mid-BC', at: mBC, style: { className: 'geo-point-main' } },
              { kind: 'point', id: 'circ-mid-AC', at: mAC, style: { className: 'geo-point-main' } },
              { kind: 'point', id: 'circ-mid-AB', at: mAB, style: { className: 'geo-point-main' } },
              { kind: 'point', id: 'circumcenter', at: cc, style: { className: 'geo-point-main' } },
              {
                kind: 'segment',
                id: 'pbisect-AB',
                from: 'circ-mid-AB',
                to: 'circumcenter',
                style: { className: 'geo-line', dashed: true },
              },
              {
                kind: 'segment',
                id: 'pbisect-BC',
                from: 'circ-mid-BC',
                to: 'circumcenter',
                style: { className: 'geo-line', dashed: true },
              },
              {
                kind: 'segment',
                id: 'pbisect-AC',
                from: 'circ-mid-AC',
                to: 'circumcenter',
                style: { className: 'geo-line', dashed: true },
              },
              {
                kind: 'circle',
                id: 'circumcircle',
                center: 'circumcenter',
                through: 'vA',
                style: { className: 'geo-line', dashed: true },
              },
            );
          }

          if (state.showIncenter) {
            // Angle bisectors from each vertex to incenter
            elems.push(
              { kind: 'point', id: 'incenter', at: ic, style: { className: 'geo-point-main' } },
              {
                kind: 'segment',
                id: 'abisect-A',
                from: 'vA',
                to: 'incenter',
                style: { className: 'geo-line', dashed: true },
              },
              {
                kind: 'segment',
                id: 'abisect-B',
                from: 'vB',
                to: 'incenter',
                style: { className: 'geo-line', dashed: true },
              },
              {
                kind: 'segment',
                id: 'abisect-C',
                from: 'vC',
                to: 'incenter',
                style: { className: 'geo-line', dashed: true },
              },
            );
          }

          if (state.showOrthocenter) {
            // Altitudes: from each vertex perpendicular to opposite side
            elems.push(
              { kind: 'point', id: 'orthocenter', at: oc, style: { className: 'geo-point-main' } },
              {
                kind: 'segment',
                id: 'alt-A',
                from: 'vA',
                to: 'orthocenter',
                style: { className: 'geo-line', dashed: true },
              },
              {
                kind: 'segment',
                id: 'alt-B',
                from: 'vB',
                to: 'orthocenter',
                style: { className: 'geo-line', dashed: true },
              },
              {
                kind: 'segment',
                id: 'alt-C',
                from: 'vC',
                to: 'orthocenter',
                style: { className: 'geo-line', dashed: true },
              },
            );
          }

          // Euler line: only when all three (centroid, circumcenter, orthocenter) are on AND circumcenter non-null
          if (state.showCentroid && state.showCircumcenter && state.showOrthocenter && cc) {
            elems.push({
              kind: 'line',
              id: 'euler-line',
              through: ['centroid', 'orthocenter'],
              style: { className: 'geo-line-special' },
            });
          }
        }

        return elems;
      }

      function buildFreeDragMarks(state: TriangleState): MarkSpec[] {
        const { vA, vB, vC } = state;
        const tri: Triangle = { a: vA, b: vB, c: vC };
        const ab = dist(vA, vB);
        const bc = dist(vB, vC);
        const ac = dist(vA, vC);
        const [angA, angB, angC] = triangleAngles(tri);
        const specs: MarkSpec[] = [];

        if (state.showMeasurements) {
          // Angle arcs
          specs.push(
            {
              kind: 'angle',
              id: 'ang-A',
              vertex: vA,
              from: vB,
              to: vC,
              label: `${angA.toFixed(1)}°`,
            },
            {
              kind: 'angle',
              id: 'ang-B',
              vertex: vB,
              from: vA,
              to: vC,
              label: `${angB.toFixed(1)}°`,
            },
            {
              kind: 'angle',
              id: 'ang-C',
              vertex: vC,
              from: vA,
              to: vB,
              label: `${angC.toFixed(1)}°`,
            },
          );
        }

        // Congruence ticks: group sides by equality
        const eps = 0.5;
        const sideGroups: { a: Vec2; b: Vec2; len: number }[][] = [];
        const sides = [
          { a: vA, b: vB, len: ab },
          { a: vB, b: vC, len: bc },
          { a: vA, b: vC, len: ac },
        ];
        for (const side of sides) {
          let placed = false;
          for (const group of sideGroups) {
            if (Math.abs(group[0]!.len - side.len) < eps) {
              group.push(side);
              placed = true;
              break;
            }
          }
          if (!placed) {
            sideGroups.push([side]);
          }
        }
        // Only add ticks for groups with >1 members (equal sides)
        for (let g = 0; g < sideGroups.length; g++) {
          const group = sideGroups[g]!;
          if (group.length > 1) {
            const count = (g + 1) as 1 | 2 | 3;
            for (let s = 0; s < group.length; s++) {
              const side = group[s]!;
              specs.push({
                kind: 'tick',
                id: `tick-g${g}-s${s}`,
                a: side.a,
                b: side.b,
                count,
              });
            }
          }
        }

        return specs;
      }

      function buildFreeDragReadout(state: TriangleState): ReadoutRow[] {
        const { vA, vB, vC } = state;
        const tri: Triangle = { a: vA, b: vB, c: vC };
        const ab = dist(vA, vB);
        const bc = dist(vB, vC);
        const ac = dist(vA, vC);
        const [angA, angB, angC] = triangleAngles(tri);
        const area = heron(ab, bc, ac);

        const rows: ReadoutRow[] = [];
        if (state.showMeasurements) {
          rows.push(
            { id: 'side-ab', lhs: 'AB', relation: '=', rhs: ab.toFixed(1), emphasis: 'neutral' },
            { id: 'side-bc', lhs: 'BC', relation: '=', rhs: bc.toFixed(1), emphasis: 'neutral' },
            { id: 'side-ac', lhs: 'AC', relation: '=', rhs: ac.toFixed(1), emphasis: 'neutral' },
            {
              id: 'ang-A-row',
              lhs: '∠A',
              relation: '=',
              rhs: `${angA.toFixed(1)}°`,
              emphasis: 'neutral',
            },
            {
              id: 'ang-B-row',
              lhs: '∠B',
              relation: '=',
              rhs: `${angB.toFixed(1)}°`,
              emphasis: 'neutral',
            },
            {
              id: 'ang-C-row',
              lhs: '∠C',
              relation: '=',
              rhs: `${angC.toFixed(1)}°`,
              emphasis: 'neutral',
            },
            {
              id: 'area-row',
              lhs: 'Area',
              relation: '=',
              rhs: area.toFixed(1),
              emphasis: 'neutral',
            },
          );
          // Angle sum invariant
          rows.push({
            id: 'ang-sum',
            lhs: '∠A+∠B+∠C',
            relation: '=',
            rhs: '180°',
            emphasis: 'invariant',
          });
        }
        return rows;
      }

      // --------------------------------------------------------------------
      // Inequality mode (hinge sweep driven by the closure `hingeT`)
      // --------------------------------------------------------------------
      function renderInequality(state: TriangleState): void {
        const { elems, rows } = hingeGeometry(
          state.inequalityA,
          state.inequalityB,
          state.inequalityC,
          hingeT,
        );
        figure.update(elems);
        marks.update([]);
        readout.update(rows);
      }

      function hingeGeometry(
        a: number,
        b: number,
        c: number,
        t: number,
      ): {
        elems: FigureElement[];
        rows: ReadoutRow[];
      } {
        // Derive the final arm angles and base decomposition via the pure helper.
        const hinge = deriveHingeArmAngles(a, b, c);
        const { base, arm1, arm2, closes, linearGap } = hinge;

        // Scale so that the base fits comfortably on stage.
        const maxBasePixels = STAGE_W * 0.6;
        const scale_ = Math.min(40, maxBasePixels / base);
        const cy = STAGE_H / 2;
        const totalSpan = base * scale_;
        const startX = (STAGE_W - totalSpan) / 2;
        const ptA: Vec2 = { x: startX, y: cy };
        const ptB: Vec2 = { x: startX + totalSpan, y: cy };

        const elems: FigureElement[] = [
          { kind: 'point', id: 'ineq-A', at: ptA, style: { className: 'geo-point-main' } },
          { kind: 'point', id: 'ineq-B', at: ptB, style: { className: 'geo-point-main' } },
          {
            kind: 'segment',
            id: 'ineq-base',
            from: 'ineq-A',
            to: 'ineq-B',
            style: { className: 'geo-line' },
          },
        ];

        const rows: ReadoutRow[] = [];

        // Easing function (ease-out cubic)
        const ease = (x: number): number => 1 - Math.pow(1 - x, 3);
        const et = ease(t);

        // Arms start pointing straight up (π/2) and sweep to their derived final angles.
        const startAngle = Math.PI / 2;
        const animAngleA = startAngle + (hinge.angleA - startAngle) * et;
        const animAngleB = startAngle + (hinge.angleB - startAngle) * et;

        const arm1End: Vec2 = {
          x: ptA.x + arm1 * scale_ * Math.cos(animAngleA),
          y: ptA.y - arm1 * scale_ * Math.sin(animAngleA), // SVG y-flip
        };
        const arm2End: Vec2 = {
          x: ptB.x - arm2 * scale_ * Math.cos(animAngleB),
          y: ptB.y - arm2 * scale_ * Math.sin(animAngleB),
        };

        elems.push(
          {
            kind: 'point',
            id: 'ineq-armA-end',
            at: arm1End,
            style: { className: 'geo-point-main' },
          },
          {
            kind: 'point',
            id: 'ineq-armB-end',
            at: arm2End,
            style: { className: 'geo-point-main' },
          },
          {
            kind: 'segment',
            id: 'ineq-armA',
            from: 'ineq-A',
            to: 'ineq-armA-end',
            style: { className: 'geo-line' },
          },
          {
            kind: 'segment',
            id: 'ineq-armB',
            from: 'ineq-B',
            to: 'ineq-armB-end',
            style: { className: 'geo-line' },
          },
          // Connector between arm tips: dashed gap when failing, solid closing edge when done
          {
            kind: 'segment',
            id: 'ineq-connector',
            from: 'ineq-armA-end',
            to: 'ineq-armB-end',
            style: { className: 'geo-line', dashed: !closes || t < 1 },
          },
        );

        // Readout: use the two shorter sides for the inequality statement.
        // Sort all three lengths and use the two smallest as lhs.
        const sorted = [a, b, c].slice().sort((x, y) => x - y);
        const s1 = sorted[0]!;
        const s2 = sorted[1]!;

        if (closes) {
          rows.push({
            id: 'ineq-valid',
            lhs: `${s1} + ${s2}`,
            relation: '>',
            rhs: String(base),
            emphasis: 'invariant',
          });
        } else {
          rows.push({
            id: 'ineq-fail',
            lhs: `${s1} + ${s2}`,
            relation: '≤',
            rhs: String(base),
            emphasis: 'broken',
          });
          rows.push({
            id: 'ineq-gap-row',
            lhs: 'gap',
            relation: '=',
            rhs: `${linearGap.toFixed(1)}`,
            emphasis: 'neutral',
          });
        }

        return { elems, rows };
      }

      // --------------------------------------------------------------------
      // Congruence mode (reads sticky positions from state)
      // --------------------------------------------------------------------
      function renderCongruence(state: TriangleState): void {
        const elems: FigureElement[] = [];
        const markSpecs: MarkSpec[] = [];
        const rows: ReadoutRow[] = [];

        if (state.congruenceCriterion === 'SSA' && state.ssaCandidates.length >= 2) {
          // Render both SSA candidates with offset ghosts.
          // The primary (t1) triangle has draggable vertices for rigid-translate.
          const t1 = state.ssaCandidates[0]!;
          const t2 = state.ssaCandidates[1]!;

          // First triangle (primary) — draggable for rigid motion
          elems.push(
            {
              kind: 'point',
              id: 'ssa-A1',
              at: t1.a,
              draggable: {},
              style: { className: 'geo-point-draggable' },
            },
            {
              kind: 'point',
              id: 'ssa-B1',
              at: t1.b,
              draggable: {},
              style: { className: 'geo-point-draggable' },
            },
            {
              kind: 'point',
              id: 'ssa-C1',
              at: t1.c,
              draggable: {},
              style: { className: 'geo-point-draggable' },
            },
            {
              kind: 'polygon',
              id: 'ssa-poly1',
              vertices: ['ssa-A1', 'ssa-B1', 'ssa-C1'],
              style: { className: 'geo-polygon-fill' },
            },
            {
              kind: 'segment',
              id: 'ssa-AB1',
              from: 'ssa-A1',
              to: 'ssa-B1',
              style: { className: 'geo-line' },
            },
            {
              kind: 'segment',
              id: 'ssa-BC1',
              from: 'ssa-B1',
              to: 'ssa-C1',
              style: { className: 'geo-line' },
            },
            {
              kind: 'segment',
              id: 'ssa-AC1',
              from: 'ssa-A1',
              to: 'ssa-C1',
              style: { className: 'geo-line' },
            },
          );

          // Second triangle — offset down on stage to distinguish
          const offset: Vec2 = { x: 0, y: 20 };
          const t2a: Vec2 = add(t2.a, offset);
          const t2b: Vec2 = add(t2.b, offset);
          const t2c: Vec2 = add(t2.c, offset);
          elems.push(
            { kind: 'point', id: 'ssa-A2', at: t2a, style: { className: 'geo-point-secondary' } },
            { kind: 'point', id: 'ssa-B2', at: t2b, style: { className: 'geo-point-secondary' } },
            { kind: 'point', id: 'ssa-C2', at: t2c, style: { className: 'geo-point-secondary' } },
            {
              kind: 'polygon',
              id: 'ssa-poly2',
              vertices: ['ssa-A2', 'ssa-B2', 'ssa-C2'],
              style: { className: 'geo-polygon-ghost' },
            },
            {
              kind: 'segment',
              id: 'ssa-AB2',
              from: 'ssa-A2',
              to: 'ssa-B2',
              style: { className: 'geo-line', dashed: true },
            },
            {
              kind: 'segment',
              id: 'ssa-BC2',
              from: 'ssa-B2',
              to: 'ssa-C2',
              style: { className: 'geo-line', dashed: true },
            },
            {
              kind: 'segment',
              id: 'ssa-AC2',
              from: 'ssa-A2',
              to: 'ssa-C2',
              style: { className: 'geo-line', dashed: true },
            },
          );

          // Locked marks for SSA: angle at A, sides AB and BC — derived from congruenceLockedParts
          const ssaLocked = congruenceLockedParts('SSA');
          markSpecs.push({
            kind: 'angle',
            id: 'lock-ang-A',
            vertex: t1.a,
            from: t1.b,
            to: t1.c,
            strokes: 2,
          });
          for (let i = 0; i < ssaLocked.lockedSides.length; i++) {
            const sideName = ssaLocked.lockedSides[i]!;
            const tickCount = (i + 1) as 1 | 2 | 3;
            if (sideName === 'sideAB') {
              markSpecs.push({
                kind: 'tick',
                id: `lock-${sideName}`,
                a: t1.a,
                b: t1.b,
                count: tickCount,
              });
            } else if (sideName === 'sideBC') {
              markSpecs.push({
                kind: 'tick',
                id: `lock-${sideName}`,
                a: t1.b,
                b: t1.c,
                count: tickCount,
              });
            }
          }

          rows.push(
            {
              id: 'ssa-count',
              lhs: 'solutions',
              relation: '=',
              rhs: `${state.ssaCandidates.length}`,
              emphasis: 'invariant',
            },
            {
              id: 'ssa-criterion',
              lhs: 'criterion',
              relation: '=',
              rhs: 'SSA (ambiguous)',
              emphasis: 'neutral',
            },
          );
        } else if (state.congruenceTri) {
          const t = state.congruenceTri;

          // Primary triangle — draggable vertices for rigid-translate motion
          elems.push(
            {
              kind: 'point',
              id: 'con-A',
              at: t.a,
              draggable: {},
              style: { className: 'geo-point-draggable' },
            },
            {
              kind: 'point',
              id: 'con-B',
              at: t.b,
              draggable: {},
              style: { className: 'geo-point-draggable' },
            },
            {
              kind: 'point',
              id: 'con-C',
              at: t.c,
              draggable: {},
              style: { className: 'geo-point-draggable' },
            },
            {
              kind: 'polygon',
              id: 'con-poly',
              vertices: ['con-A', 'con-B', 'con-C'],
              style: { className: 'geo-polygon-fill' },
            },
            {
              kind: 'segment',
              id: 'con-AB',
              from: 'con-A',
              to: 'con-B',
              style: { className: 'geo-line' },
            },
            {
              kind: 'segment',
              id: 'con-BC',
              from: 'con-B',
              to: 'con-C',
              style: { className: 'geo-line' },
            },
            {
              kind: 'segment',
              id: 'con-AC',
              from: 'con-A',
              to: 'con-C',
              style: { className: 'geo-line' },
            },
          );

          // Ghost copy: fixed offset from primary (moves rigidly with it)
          const gOffset: Vec2 = { x: 30, y: -20 };
          const gA = add(t.a, gOffset);
          const gB = add(t.b, gOffset);
          const gC = add(t.c, gOffset);
          elems.push(
            { kind: 'point', id: 'ghost-A', at: gA, style: { className: 'geo-point-secondary' } },
            { kind: 'point', id: 'ghost-B', at: gB, style: { className: 'geo-point-secondary' } },
            { kind: 'point', id: 'ghost-C', at: gC, style: { className: 'geo-point-secondary' } },
            {
              kind: 'polygon',
              id: 'ghost-poly',
              vertices: ['ghost-A', 'ghost-B', 'ghost-C'],
              style: { className: 'geo-polygon-ghost' },
            },
          );

          // Locked marks derived from congruenceLockedParts
          for (const spec of buildCongruenceMarkSpecs(t, state.congruenceCriterion)) {
            markSpecs.push(spec);
          }

          const ab = dist(t.a, t.b);
          const bc = dist(t.b, t.c);
          const ac = dist(t.a, t.c);
          const [angA, angB, angC] = triangleAngles(t);

          rows.push(
            {
              id: 'con-criterion',
              lhs: 'criterion',
              relation: '=',
              rhs: state.congruenceCriterion,
              emphasis: 'invariant',
            },
            { id: 'con-AB', lhs: 'AB', relation: '=', rhs: ab.toFixed(1), emphasis: 'neutral' },
            { id: 'con-BC', lhs: 'BC', relation: '=', rhs: bc.toFixed(1), emphasis: 'neutral' },
            { id: 'con-AC', lhs: 'AC', relation: '=', rhs: ac.toFixed(1), emphasis: 'neutral' },
            {
              id: 'con-angA',
              lhs: '∠A',
              relation: '=',
              rhs: `${angA.toFixed(1)}°`,
              emphasis: 'neutral',
            },
            {
              id: 'con-angB',
              lhs: '∠B',
              relation: '=',
              rhs: `${angB.toFixed(1)}°`,
              emphasis: 'neutral',
            },
            {
              id: 'con-angC',
              lhs: '∠C',
              relation: '=',
              rhs: `${angC.toFixed(1)}°`,
              emphasis: 'neutral',
            },
          );
        }

        figure.update(elems);
        marks.update(markSpecs);
        readout.update(rows);
      }
    },

    panel(state: TriangleState, ctx): PanelRow<GeometryKey, TriangleState>[] {
      const rows: PanelRow<GeometryKey, TriangleState>[] = [];

      // Mode dropdown
      rows.push({
        kind: 'dropdown',
        id: 'triangle-mode',
        labelKey: 'panel.mode',
        options: [
          { value: 'free-drag', labelKey: 'triangle.mode.free-drag' },
          { value: 'inequality', labelKey: 'triangle.mode.inequality' },
          { value: 'congruence', labelKey: 'triangle.mode.congruence' },
        ],
        value: state.mode,
        onChange: (v) => {
          const mode = v as TriangleMode;
          if (mode === 'inequality') startHingeAnimation();
          return { ...state, mode };
        },
      });

      // Criterion picker (congruence mode only)
      if (state.mode === 'congruence') {
        rows.push({
          kind: 'dropdown',
          id: 'triangle-criterion',
          labelKey: 'triangle.criterion',
          options: CRITERIA.map((c) => ({
            value: c,
            labelKey: `triangle.criterion.${c}` as GeometryKey,
          })),
          value: state.congruenceCriterion,
          onChange: (v) => {
            const criterion = v as CongruenceCriterion;
            const realized = realizeCongruence(criterion);
            return {
              ...state,
              congruenceCriterion: criterion,
              congruenceTri: realized.congruenceTri,
              ssaCandidates: realized.ssaCandidates,
            };
          },
        });
      }

      // Inequality sliders (inequality mode only). Labels are the symbolic
      // single-letter variable names a/b/c (translation-independent), matching
      // the bespoke literal labels; the value display mirrors String(v).
      if (state.mode === 'inequality') {
        const sliderRow = (
          id: string,
          label: string,
          value: number,
          set: (v: number) => TriangleState,
        ): PanelRow<GeometryKey, TriangleState> => ({
          kind: 'slider',
          id: `triangle-ineq-${id}`,
          labelKey: label as GeometryKey,
          min: 1,
          max: 10,
          step: 0.5,
          value,
          format: (v) => String(v),
          onChange: (v) => {
            const next = set(v);
            startHingeAnimation();
            return next;
          },
        });
        rows.push(
          sliderRow('a', 'a', state.inequalityA, (v) => ({ ...state, inequalityA: v })),
          sliderRow('b', 'b', state.inequalityB, (v) => ({ ...state, inequalityB: v })),
          sliderRow('c', 'c', state.inequalityC, (v) => ({ ...state, inequalityC: v })),
        );
      }

      // Show measurements toggle (always present)
      rows.push({
        kind: 'toggle',
        labelKey: 'triangle.panel.show-measurements',
        value: state.showMeasurements,
        onChange: (v) => ({ ...state, showMeasurements: v }),
      });

      // Advanced centre toggles (free-drag + advanced only)
      if (ctx.advanced && state.mode === 'free-drag') {
        rows.push(
          {
            kind: 'toggle',
            labelKey: 'triangle.panel.center-centroid',
            value: state.showCentroid,
            onChange: (v) => ({ ...state, showCentroid: v }),
          },
          {
            kind: 'toggle',
            labelKey: 'triangle.panel.center-circumcenter',
            value: state.showCircumcenter,
            onChange: (v) => ({ ...state, showCircumcenter: v }),
          },
          {
            kind: 'toggle',
            labelKey: 'triangle.panel.center-incenter',
            value: state.showIncenter,
            onChange: (v) => ({ ...state, showIncenter: v }),
          },
          {
            kind: 'toggle',
            labelKey: 'triangle.panel.center-orthocenter',
            value: state.showOrthocenter,
            onChange: (v) => ({ ...state, showOrthocenter: v }),
          },
        );
      }

      // Advanced toggle (harness owns persistence + re-render)
      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      return rows;
    },

    actions: (state: TriangleState, ctx): ActionBarSpec<GeometryKey, TriangleState> => ({
      onReset: () => {
        const next = defaultState(ctx.advanced);
        // Reset keeps the current mode (bespoke onReset re-rendered current mode
        // after resetting the per-mode params + criterion to defaults).
        const reset: TriangleState = { ...next, mode: state.mode };
        if (reset.mode === 'inequality') startHingeAnimation();
        return reset;
      },
      onRandomise: () => {
        if (state.mode === 'free-drag') {
          // Resample until no angle < 15°
          const margin = 80;
          const maxAngle = 15;
          let attempts = 0;
          let vA = state.vA;
          let vB = state.vB;
          let vC = state.vC;
          let ok = false;
          while (!ok && attempts < 100) {
            attempts++;
            const rA: Vec2 = {
              x: margin + ctx.prng() * (STAGE_W - 2 * margin),
              y: margin + ctx.prng() * (STAGE_H - 2 * margin),
            };
            const rB: Vec2 = {
              x: margin + ctx.prng() * (STAGE_W - 2 * margin),
              y: margin + ctx.prng() * (STAGE_H - 2 * margin),
            };
            const rC: Vec2 = {
              x: margin + ctx.prng() * (STAGE_W - 2 * margin),
              y: margin + ctx.prng() * (STAGE_H - 2 * margin),
            };
            const [a, b, c] = triangleAngles({ a: rA, b: rB, c: rC });
            if (a >= maxAngle && b >= maxAngle && c >= maxAngle) {
              vA = rA;
              vB = rB;
              vC = rC;
              ok = true;
            }
          }
          return { ...state, vA, vB, vC };
        } else if (state.mode === 'inequality') {
          const next: TriangleState = {
            ...state,
            inequalityA: 1 + Math.floor(ctx.prng() * 9),
            inequalityB: 1 + Math.floor(ctx.prng() * 9),
            inequalityC: 1 + Math.floor(ctx.prng() * 9),
          };
          startHingeAnimation();
          return next;
        } else if (state.mode === 'congruence') {
          const idx = Math.floor(ctx.prng() * CRITERIA.length);
          const criterion = CRITERIA[idx]!;
          const realized = realizeCongruence(criterion);
          return {
            ...state,
            congruenceCriterion: criterion,
            congruenceTri: realized.congruenceTri,
            ssaCandidates: realized.ssaCandidates,
          };
        }
        return state;
      },
      presets: TRIANGLE_PRESETS.filter(
        (p) => p.mode === state.mode && (ctx.advanced || !p.advancedOnly),
      ).map((p) => ({ id: p.id, labelKey: p.labelKey })),
      onPreset: (s, id) => {
        const next = applyPresetLocal(s, id, ctx.advanced);
        if (next.mode === 'inequality' && next !== s) startHingeAnimation();
        return next;
      },
    }),

    onDrag(state: TriangleState, pointId: string, at: Vec2): TriangleState {
      if (state.mode === 'free-drag') {
        if (pointId === 'vA') return { ...state, vA: at };
        if (pointId === 'vB') return { ...state, vB: at };
        if (pointId === 'vC') return { ...state, vC: at };
      } else if (state.mode === 'congruence') {
        // Rigid-translate: when any congruence vertex is dragged, compute the
        // pointer delta from its current registered position and translate the
        // whole triangle by that delta (preserves all side lengths).
        if (pointId === 'con-A' || pointId === 'con-B' || pointId === 'con-C') {
          const t = state.congruenceTri;
          if (!t) return state;
          const prev = pointId === 'con-A' ? t.a : pointId === 'con-B' ? t.b : t.c;
          const dx = at.x - prev.x;
          const dy = at.y - prev.y;
          return {
            ...state,
            congruenceTri: {
              a: { x: t.a.x + dx, y: t.a.y + dy },
              b: { x: t.b.x + dx, y: t.b.y + dy },
              c: { x: t.c.x + dx, y: t.c.y + dy },
            },
          };
        } else if (pointId === 'ssa-A1' || pointId === 'ssa-B1' || pointId === 'ssa-C1') {
          const t = state.ssaCandidates[0];
          if (!t) return state;
          const prev = pointId === 'ssa-A1' ? t.a : pointId === 'ssa-B1' ? t.b : t.c;
          const dx = at.x - prev.x;
          const dy = at.y - prev.y;
          const moved: Triangle = {
            a: { x: t.a.x + dx, y: t.a.y + dy },
            b: { x: t.b.x + dx, y: t.b.y + dy },
            c: { x: t.c.x + dx, y: t.c.y + dy },
          };
          const ssaCandidates = state.ssaCandidates.slice();
          ssaCandidates[0] = moved;
          return { ...state, ssaCandidates, congruenceTri: moved };
        }
      }
      // inequality mode has no drag points
      return state;
    },

    applyShotState(state: TriangleState, shotId: string): TriangleState {
      if (shotId === 'triangle-lab') {
        // The shot = euler-tour preset (advancedOnly) with ALL FOUR centre
        // toggles on. The euler-tour preset already turns on centroid +
        // circumcenter + orthocenter; we additionally enable incenter so all
        // four centres show (the bespoke shot clicked toggle indices 1..4).
        // Apply as if advanced so the advancedOnly preset takes effect.
        const posed = applyPresetLocal(state, 'euler-tour', true);
        return {
          ...posed,
          showCentroid: true,
          showCircumcenter: true,
          showIncenter: true,
          showOrthocenter: true,
        };
      }
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// ============================================================================
// Helper re-exports for testing (vec operations used in tests)
// ============================================================================
export { dist, add, sub, scale, normalize };
export { heron };
export {
  centroid as triCentroid,
  circumcenter as triCircumcenter,
  incenter as triIncenter,
  orthocenter as triOrthocenter,
};
